import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from './ui/Spinner';
import { showSessionExpiredNotification } from '../utils/toast-helper';

import { useToolsApi } from '../apis/tools-apis';
import { useAgentsApi } from '../apis/agents-apis';
import { usePlaybooksApi } from '../apis/playbooks-apis';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  entityType?: 'agent' | 'tool' | 'playbook';
}

interface AccessControl {
  editAccessType: 'All Users' | 'Allowed Users' | 'Owner Only';
  editAllowedUsers: string[];
  viewAccessType: 'All Users' | 'Allowed Users' | 'Owner Only';
  viewAllowedUsers: string[];
}

interface Entity {
  ownerId: string;
  accessControl: AccessControl;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  entityType 
}) => {
  const { isAuthenticated, isLoading, isAdmin, user, signOut } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(true);

  const toolsApi = useToolsApi();
  const agentsApi = useAgentsApi();
  const playbooksApi = usePlaybooksApi();

  // Check token validity on component mount
  useEffect(() => {
    const checkTokenValidity = () => {
      try {
        const idToken = localStorage.getItem('idToken');
        
        if (!idToken) {
          // No token means not authenticated
          setIsTokenValid(false);
          return;
        }
        
        // Decode token to check expiration
        const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (idTokenPayload.exp < currentTime) {
          console.log('Token expired in ProtectedRoute');
          
          // Show notification for expired session
          showSessionExpiredNotification();
          
          setIsTokenValid(false);
          
          // Clear stored tokens and sign out
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('authToken');
          
          signOut();
        } else {
          setIsTokenValid(true);
        }
      } catch (error) {
        console.error('Error checking token validity:', error);
        setIsTokenValid(false);
      }
    };

    // Check immediately on mount
    checkTokenValidity();
    
    // Set up listener for window focus to recheck token
    const handleFocus = () => {
      checkTokenValidity();
    };
    
    window.addEventListener('focus', handleFocus);
    
    // Clean up
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [signOut]);

  const checkAccess = (
    entity: Entity,
    userId: string,
    isAdmin: boolean
  ): boolean => {
    // Admins always have access
    if (isAdmin) {
      return true;
    }
  
    // Entity owners always have access
    if (entity.ownerId === userId) {
      return true;
    }
  
    const hasEditAccess = (() => {
      switch (entity.accessControl.editAccessType) {
        case 'All Users':
          return true;
        case 'Allowed Users':
          return entity.accessControl.editAllowedUsers.includes(userId);
        case 'Owner Only':
        default:
          return false;
      }
    })();
  
    // If user has edit access, they automatically have view access
    if (hasEditAccess) {
      return true;
    }
  
    // If user doesn't have edit access, check view access
    const hasViewAccess = (() => {
      switch (entity.accessControl.viewAccessType) {
        case 'All Users':
          return true;
        case 'Allowed Users':
          return entity.accessControl.viewAllowedUsers.includes(userId);
        case 'Owner Only':
        default:
          return false;
      }
    })();
  
    return hasViewAccess;
  };

  useEffect(() => {
    const checkEntityAccess = async () => {
      // Only check access if we're dealing with an entity and have an ID
      if (!entityType || !id || !user) return;

      // Admins always have access
      if (isAdmin) return;

      setIsCheckingAccess(true);
      try {
        let entity;
        console.log("Getting entity");
        console.log("Entity type:", entityType);
        console.log("ID:", id);
        console.log("User:", user);
        switch (entityType) {
          case 'tool':
            console.log("Getting tool");
            entity = await toolsApi.getTool(id);
            console.log("Tool received");
            break;
          case 'agent':
            console.log("Getting agent");
            entity = await agentsApi.getAgent(id);
            console.log("Agent received");
            break;
          case 'playbook':
            console.log("Getting playbook");
            entity = await playbooksApi.getPlaybook(id);
            console.log("Playbook received");
            break;
          default:
            console.log("Unknown entity type");
            setHasAccess(false);
            return;
        }

        console.log("Entity:", entity);

        if (!entity) {
          console.log("Entity not found");
          setHasAccess(false);
          return;
        }

        const hasEntityAccess = checkAccess(entity, user.userId, isAdmin);
        setHasAccess(hasEntityAccess);
      } catch (error) {
        console.error('Error checking entity access:', error);
        setHasAccess(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkEntityAccess();
  }, [id, entityType, user, isAdmin, location.pathname]);

  if (isLoading || isCheckingAccess) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">
          {isCheckingAccess ? 'Checking access...' : 'Loading Studio...'}
        </p>
      </div>
    );
  }

  // Check for invalid token first
  if (!isTokenValid) {
    console.log("Token invalid, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  if (!isAuthenticated) {
    console.log("Not authenticated");
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !isAdmin) {
    console.log("Not admin");
    return <Navigate to="/studio/unauthorized" replace />;
  }

  if (!hasAccess) {
    console.log("Not authorized");
    return <Navigate to="/studio/unauthorized" replace />;
  }

  return <>{children}</>;
};